import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { oceanApi } from "./services/oceanApi";
import { tomtomApi } from "./services/tomtomApi";

const BASE = "https://support.gs-group.fr/api";
const tok  = () => localStorage.getItem("nexo_token");
const h    = () => ({ Authorization: "Bearer " + tok() });
const hj   = () => ({ ...h(), "Content-Type": "application/json" });

// CARTO a fermé ses tuiles anonymes gratuites (cartocdn.com exige désormais une clé API,
// sans quoi chaque tuile affiche un watermark "API KEY REQUIRED"). Basculé sur les fonds
// de carte publics Esri (aucune clé requise).
const TILES = {
  dark:  "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
  light: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
};
const ATTR   = '© <a href="https://www.esri.com/">Esri</a>';

// ── Statuts flotte Océan : vert = en mouvement, bleu = à l'arrêt/en intervention, gris = inactif/hors service ──
const FLEET_STATUS_COLOR = { en_trajet: "#22c55e", en_intervention: "#3b82f6", disponible: "#94a3b8", hors_service: "#475569" };
const FLEET_STATUS_LABEL = { en_trajet: "En trajet", en_intervention: "En intervention", disponible: "Disponible", hors_service: "Hors service" };

// ── Couleurs statuts ──
const SC = { planifiee:"#f5a623", pending:"#f5a623", en_cours:"#5b6ef8", "in-progress":"#5b6ef8",
             terminee:"#22d3a0", completed:"#22d3a0", annulee:"#ef4444" };
const SL = { planifiee:"Planifié", pending:"Planifié", en_cours:"En cours", "in-progress":"En cours",
             terminee:"Terminé", completed:"Terminé", annulee:"Annulé" };
const PC = { urgente:"#ef4444", haute:"#f59e0b", normale:"#5b6ef8", basse:"#22d3a0" };

const COLORS = ["#5b6ef8","#22d3a0","#f5a623","#a855f7","#06b6d4","#f43f5e","#84cc16","#ec4899"];

const CSS_ANIM = `
  @keyframes tech-ping   { 0%,100%{opacity:0} 50%{opacity:.45} }
  @keyframes sav-pulse   { 0%,100%{transform:scale(1);opacity:.45} 70%{transform:scale(1.9);opacity:0} }
  @keyframes route-dash  { to { stroke-dashoffset: -20; } }
  @keyframes fleet-ping  { 0%{opacity:.55; transform:scale(.6)} 100%{opacity:0; transform:scale(1.6)} }
  @keyframes fleet-pop   { 0%{transform:scale(.2); opacity:0} 60%{transform:scale(1.12); opacity:1} 100%{transform:scale(1); opacity:1} }
  .fleet-marker-pop { animation: fleet-pop .4s cubic-bezier(.34,1.56,.64,1) both; }
  .leaflet-popup-content-wrapper { border-radius:14px !important; box-shadow:0 8px 32px rgba(0,0,0,.18) !important; }
  .leaflet-popup-tip { display:none !important; }
`;

// ── Icônes Leaflet ──
const mkTechIcon = (L, color, init, active) => L.divIcon({
  html:`<div style="position:relative;filter:drop-shadow(0 4px 10px rgba(0,0,0,.35))">
    ${active?`<div style="position:absolute;inset:-6px;border-radius:50%;border:2px solid ${color};animation:tech-ping 2s ease-in-out infinite;pointer-events:none"></div>`:""}
    <div style="width:44px;height:44px;border-radius:50%;background:${color};border:3px solid #fff;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:900;color:#fff;position:relative;z-index:1">${init}</div>
    <div style="width:0;height:0;border-left:10px solid transparent;border-right:10px solid transparent;border-top:12px solid ${color};margin:0 auto;margin-top:-3px"></div>
  </div>`,
  className:"", iconSize:[44,60], iconAnchor:[22,60], popupAnchor:[0,-62],
});

const mkIntIcon = (L, color, emoji="🔧", big=false) => {
  const s = big ? 40 : 34;
  return L.divIcon({
    html:`<div style="width:${s}px;height:${s}px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 3px 12px rgba(0,0,0,.28);display:flex;align-items:center;justify-content:center">
      <span style="transform:rotate(45deg);font-size:${Math.round(s*.4)}px;line-height:1">${emoji}</span></div>`,
    className:"", iconSize:[s,s], iconAnchor:[s/2,s], popupAnchor:[0,-(s+4)],
  });
};

const mkSAVIcon = (L, color) => L.divIcon({
  html:`<div style="position:relative;width:34px;height:34px">
    <div style="position:absolute;inset:0;border-radius:50%;background:${color};animation:sav-pulse 1.9s ease-out infinite"></div>
    <div style="position:absolute;inset:6px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;font-size:12px">🎫</div>
  </div>`,
  className:"", iconSize:[34,34], iconAnchor:[17,17], popupAnchor:[0,-18],
});

const mkNumIcon = (L, color, n) => L.divIcon({
  html:`<div style="width:22px;height:22px;border-radius:50%;background:${color};color:#fff;font-size:11px;font-weight:900;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">${n}</div>`,
  className:"", iconSize:[22,22], iconAnchor:[11,11],
});

const mkFleetIcon = (L, color, speed, active, isNew) => L.divIcon({
  html:`<div style="position:relative;filter:drop-shadow(0 4px 10px rgba(0,0,0,.4))">
    ${active?`<div style="position:absolute;inset:-6px;border-radius:50%;border:2px solid ${color};animation:fleet-ping 1.8s cubic-bezier(.2,.7,.4,1) infinite;pointer-events:none"></div>`:""}
    <div style="width:40px;height:40px;border-radius:50%;background:${color};border:3px solid #fff;display:flex;align-items:center;justify-content:center;font-size:16px;position:relative;z-index:1">🚐</div>
    ${speed>3?`<div style="position:absolute;top:-6px;right:-6px;background:#0f172a;color:#fff;font-size:9px;font-weight:800;padding:1px 5px;border-radius:8px;border:1.5px solid #fff">${Math.round(speed)}</div>`:""}
    <div style="width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:11px solid ${color};margin:0 auto;margin-top:-3px"></div>
  </div>`,
  className: isNew ? "fleet-marker-pop" : "", iconSize:[40,56], iconAnchor:[20,56], popupAnchor:[0,-58],
});

/** Glisse en douceur un marker vers sa nouvelle position au lieu d'un saut instantané. */
function animateFleetMarkerTo(L, marker, toLatLng, duration = 700) {
  const from = marker.getLatLng();
  const to = L.latLng(toLatLng);
  if (from.equals(to, 1e-6)) return;
  const start = performance.now();
  const dLat = to.lat - from.lat, dLng = to.lng - from.lng;
  function step(now) {
    const t = Math.min(1, (now - start) / duration);
    const ease = 1 - Math.pow(1 - t, 3); // ease-out cubic
    marker.setLatLng([from.lat + dLat * ease, from.lng + dLng * ease]);
    if (t < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

// ── Helpers ──
const isToday = d => {
  if(!d) return false;
  const t = new Date(), x = new Date(d);
  return x.toDateString() === t.toDateString();
};
const fmtTime = d => d ? new Date(d).toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"}) : "—";
const ageMin  = ts => ts ? Math.round((Date.now()-new Date(ts).getTime())/60000) : null;
const active  = ts => { const a = ageMin(ts); return a!==null && a<5; };
const online  = ts => { const a = ageMin(ts); return a!==null && a<90; };
const initials= (p,n) => `${(p||"?")[0]}${(n||"?")[0]}`.toUpperCase();

// ── Helpers flotte Océan ──
const fleetAgeSec   = ts => ts ? Math.round((Date.now()-new Date(ts).getTime())/1000) : null;
const fmtFleetAge   = ts => { const s=fleetAgeSec(ts); if(s==null) return "—"; if(s<60) return "À l'instant"; const m=Math.round(s/60); return m<60?`${m} min`:`${Math.round(m/60)} h`; };
const isFleetFresh  = ts => { const s=fleetAgeSec(ts); return s!==null && s<60; };
const fleetInitials = name => (name||"? ?").trim().split(/\s+/).slice(0,2).map(w=>w[0]).join("").toUpperCase();
const fuelColor = f => f==null ? "#94a3b8" : f<15 ? "#ef4444" : f<30 ? "#f59e0b" : "#22c55e";
const fmtFuel   = f => f==null ? null : `⛽ ${Math.round(f)}%`;

export default function MapView({ T, interventions=[], tickets=[], techniciens=[], currentUser }) {
  const isAdmin = currentUser?.role === "admin";
  const mapRef  = useRef(null);
  const mapInst = useRef(null);
  const lgRef   = useRef({ tech:null, int:null, sav:null, route:null, sector:null, fleet:null, fleetHistory:null });
  const tmRef   = useRef({});   // tech markers { user_id → L.marker }
  const fmRef   = useRef({});   // fleet markers { vehicle_id → L.marker }
  const colorMap= useRef({});
  const tileRef = useRef(null);
  const trafficTileRef = useRef(null);

  const [ready,        setReady]        = useState(false);
  const [layers,       setLayers]       = useState({ techs:true, interventions:true, sav:true, sectors:false, fleet:false, traffic:false });
  const [trafficAvailable, setTrafficAvailable] = useState(false);
  const [trafficZones,     setTrafficZones]     = useState([]);
  const [interventionEta,  setInterventionEta]  = useState(null); // {loading, error, data} pour l'intervention sélectionnée
  const [techPos,      setTechPos]      = useState([]);
  const [techVehs,     setTechVehs]     = useState({});
  const [techStocks,   setTechStocks]   = useState({});
  const [selectedTech, setSelectedTech] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [dragSAV,      setDragSAV]      = useState(null);
  const [sideSearch,   setSideSearch]   = useState("");
  const [myPos,        setMyPos]        = useState(null);
  const [assigning,       setAssigning]       = useState(false);
  const [toast,           setToast]           = useState(null);
  const [histPeriod,      setHistPeriod]      = useState("today"); // "today"|"week"|"month"|"all"
  const [showTerminated,  setShowTerminated]  = useState(false);
  const [techFilter,      setTechFilter]      = useState("all"); // "all" | user_id
  const [statusFilter,    setStatusFilter]    = useState("all"); // "all"|"planifiee"|"en_cours"|"terminee"
  const techStocksRef = useRef({});

  // ── Flotte Océan (fusion de l'ancienne page GPS Flotte) ──
  const [oceanTechs,    setOceanTechs]    = useState([]);
  const [oceanStatus,   setOceanStatus]   = useState(null);
  const [fleetMode,     setFleetMode]     = useState("live"); // "live" | "history"
  const [fleetSelected, setFleetSelected] = useState(null);
  const [histPlate,     setHistPlate]     = useState("");
  const [histDate,      setHistDate]      = useState(() => new Date().toISOString().slice(0,10));
  const [histPoints,    setHistPoints]    = useState([]);
  const [histLoading,   setHistLoading]   = useState(false);
  const [histError,     setHistError]     = useState(null);
  const [histLoaded,    setHistLoaded]    = useState(false);
  const [showFleetConfig,   setShowFleetConfig]   = useState(false);
  const [fleetConfigTechs,  setFleetConfigTechs]  = useState([]);
  const [fleetConfigLoading,setFleetConfigLoading]= useState(false);
  const [togglingPlate,     setTogglingPlate]     = useState(null);

  // ── Computed (mémorisé) ──
  const filteredInts = useMemo(()=>{
    // Date de référence d'une intervention : date planifiée, ou à défaut date de màj / création
    const refDate = i => i.date_planifiee || i.updated_at || i.created_at;

    const inPeriod = (i) => {
      if(histPeriod==="all") return true;
      const d = refDate(i);
      if(!d) return false;
      const dt  = new Date(d);
      const now = new Date();
      if(histPeriod==="today") return dt.toDateString()===now.toDateString();
      if(histPeriod==="week")  { const f=new Date(now); f.setDate(f.getDate()-7);    return dt>=f; }
      if(histPeriod==="month") { const f=new Date(now); f.setMonth(f.getMonth()-1);  return dt>=f; }
      return true;
    };

    return interventions.filter(i => {
      if(!inPeriod(i)) return false;
      if(!showTerminated && ["terminee","annulee"].includes(i.statut)) return false;
      if(statusFilter!=="all" && i.statut!==statusFilter) return false;
      if(techFilter!=="all" && String(i.technicien_id)!==String(techFilter)) return false;
      return true;
    });
  },[interventions, histPeriod, showTerminated, statusFilter, techFilter]);

  // todayInts reste pour les stats sidebar (toujours = aujourd'hui)
  const todayInts     = useMemo(()=>interventions.filter(i=>isToday(i.date_planifiee)),[interventions]);
  const unassignedSAV = useMemo(()=>tickets.filter(t=>!t.technicien_id&&!t.agent_id&&!["ferme","cloture","resolu"].includes(t.statut)),[tickets]);
  const unassignedInts= useMemo(()=>interventions.filter(i=>!i.technicien_id&&!["terminee","annulee"].includes(i.statut)),[interventions]);
  const today         = new Date().toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"});

  // Synchronise la ref de stock pour éviter les deps circulaires
  useEffect(()=>{ techStocksRef.current = techStocks; },[techStocks]);

  // ── Couleur stable par tech ──
  const getColor = useCallback(uid => {
    if(!colorMap.current[uid]) {
      colorMap.current[uid] = COLORS[Object.keys(colorMap.current).length % COLORS.length];
    }
    return colorMap.current[uid];
  }, []);

  // ── Data fetching ──
  const loadTechPos = useCallback(async () => {
    try {
      const d = await fetch(`${BASE}/interventions/tech-positions`,{headers:h()}).then(r=>r.json());
      if(Array.isArray(d)) setTechPos(d);
    } catch {}
  },[]);

  const loadVehs = useCallback(async () => {
    try {
      const d = await fetch(`${BASE}/vehicules`,{headers:h()}).then(r=>r.json());
      if(!Array.isArray(d)) return;
      const m = {};
      d.forEach(v => { if(v.technicien_id) m[v.technicien_id] = v; });
      setTechVehs(m);
    } catch {}
  },[]);

  const loadStock = useCallback(async (userId) => {
    if(techStocksRef.current[userId]) return; // ref = pas de dep circulaire
    const veh = techVehs[userId];
    if(!veh) return;
    try {
      const d = await fetch(`${BASE}/vehicules/${veh.id}/stock`,{headers:h()}).then(r=>r.json());
      if(Array.isArray(d)) setTechStocks(p=>({...p,[userId]:d}));
    } catch {}
  },[techVehs]);

  useEffect(()=>{
    loadTechPos(); loadVehs();
    const t = setInterval(loadTechPos, 20000);
    return () => clearInterval(t);
  },[loadTechPos, loadVehs]);

  // ── Data fetching flotte Océan ──
  const loadOceanTechs = useCallback(async () => {
    try { setOceanTechs(await oceanApi.getTechnicians()); } catch {}
  }, []);
  const loadOceanStatus = useCallback(async () => {
    try { setOceanStatus(await oceanApi.getStatus()); } catch {}
  }, []);

  useEffect(()=>{
    loadOceanTechs(); loadOceanStatus();
    const t = setInterval(()=>{ loadOceanTechs(); loadOceanStatus(); }, 30000);
    return () => clearInterval(t);
  },[loadOceanTechs, loadOceanStatus]);

  // SSE : mise à jour quasi-instantanée sur push webhook/poll Océan
  useEffect(()=>{
    const es = new EventSource(`${BASE}/events?token=${tok()}`);
    es.addEventListener("ocean_positions_updated", () => loadOceanTechs());
    return () => es.close();
  },[loadOceanTechs]);

  // ── Trafic temps réel TomTom (disponibilité + zones surveillées, configurées dans Réglages) ──
  useEffect(()=>{
    tomtomApi.isAvailable().then(setTrafficAvailable).catch(()=>{});
    tomtomApi.getZones().then(setTrafficZones).catch(()=>{});
  },[]);

  // ── Historique / parcours du jour d'un véhicule ──
  const loadFleetHistory = useCallback(async () => {
    if (!histPlate || !histDate) return;
    setHistLoading(true); setHistError(null); setHistLoaded(false);
    try {
      const pts = await oceanApi.getHistory(histPlate, histDate);
      setHistPoints(pts);
      setHistLoaded(true);
    } catch (e) {
      setHistError(e.message || "Erreur de chargement");
      setHistPoints([]);
    }
    setHistLoading(false);
  }, [histPlate, histDate]);

  useEffect(()=>{
    if (fleetMode==="history" && histPlate && histDate) loadFleetHistory();
  },[fleetMode, histPlate, histDate, loadFleetHistory]);

  const histVehicles = useMemo(() => {
    const byPlate = new Map();
    oceanTechs.forEach(tp => { if (tp.vehicle_plate) byPlate.set(tp.vehicle_plate, tp.name); });
    return [...byPlate.entries()].map(([plate, name]) => ({ plate, name })).sort((a,b)=>a.name.localeCompare(b.name));
  }, [oceanTechs]);
  const minHistDate = useMemo(() => new Date(Date.now() - 29*86400000).toISOString().slice(0,10), []);
  const todayStr     = useMemo(() => new Date().toISOString().slice(0,10), []);

  const histSummary = useMemo(() => {
    if (histPoints.length < 1) return null;
    let distanceKm = 0;
    for (let i=1; i<histPoints.length; i++) {
      const toRad = d => d*Math.PI/180;
      const dLat=toRad(histPoints[i].latitude-histPoints[i-1].latitude), dLng=toRad(histPoints[i].longitude-histPoints[i-1].longitude);
      const a = Math.sin(dLat/2)**2 + Math.cos(toRad(histPoints[i-1].latitude))*Math.cos(toRad(histPoints[i].latitude))*Math.sin(dLng/2)**2;
      distanceKm += 6371 * 2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
    }
    return {
      points: histPoints.length,
      distanceKm: Math.round(distanceKm*10)/10,
      start: histPoints[0].recorded_at,
      end: histPoints[histPoints.length-1].recorded_at,
    };
  }, [histPoints]);

  // ── Panneau de configuration flotte (admin) : afficher/masquer des véhicules sur la carte ──
  const openFleetConfig = async () => {
    setShowFleetConfig(true);
    setFleetConfigLoading(true);
    try { setFleetConfigTechs(await oceanApi.getTechnicians(true)); } catch {}
    setFleetConfigLoading(false);
  };
  const toggleFleetVehicleHidden = async (plate, hidden) => {
    if (!plate || togglingPlate) return;
    setTogglingPlate(plate);
    try {
      await oceanApi.setVehicleHidden(plate, hidden);
      setFleetConfigTechs(list => list.map(t => t.vehicle_plate===plate ? { ...t, hidden } : t));
      loadOceanTechs();
    } catch {}
    setTogglingPlate(null);
  };

  // ── Affectation d'une intervention à un véhicule de la flotte ──
  const assignInterventionToVehicle = async (tech, intervention) => {
    if (!tech || !intervention || assigning) return;
    if (!tech.technician_id) {
      setToast({ ok:false, msg:"❌ Ce véhicule n'est pas rattaché à un technicien du CRM (vérifier la plaque dans Parc Véhicules)" });
      setTimeout(()=>setToast(null), 4500);
      return;
    }
    setAssigning(true);
    try {
      const res = await fetch(`${BASE}/interventions/${intervention.id}/assigner`, {
        method:"PATCH", headers:hj(),
        body: JSON.stringify({ technicien_id: tech.technician_id, commentaire: "Assigné depuis la carte flotte" }),
      }).then(r=>r.json());
      if (res.error) throw new Error(res.error);
      setToast({ ok:true, msg:`✅ "${intervention.titre}" → ${tech.name}` });
    } catch(e) {
      setToast({ ok:false, msg:"❌ "+e.message });
    }
    setAssigning(false);
    setTimeout(()=>setToast(null), 3500);
  };

  // ── Init Leaflet ──
  useEffect(()=>{
    if(mapInst.current || !mapRef.current) return;
    const init = () => {
      const L = window.L;
      if(!L || !mapRef.current || mapInst.current) return;
      // Inject animation CSS once
      if(!document.getElementById("map-anim-css")) {
        const s = document.createElement("style"); s.id="map-anim-css"; s.textContent=CSS_ANIM;
        document.head.appendChild(s);
      }
      const map = L.map(mapRef.current,{ zoomControl:false });
      map.setView([47.75,7.35], 10);
      L.control.zoom({ position:"topright" }).addTo(map);
      lgRef.current.sector       = L.layerGroup().addTo(map);
      lgRef.current.int          = L.layerGroup().addTo(map);
      lgRef.current.sav          = L.layerGroup().addTo(map);
      lgRef.current.route        = L.layerGroup().addTo(map);
      lgRef.current.tech         = L.layerGroup().addTo(map);
      lgRef.current.fleet        = L.layerGroup();
      lgRef.current.fleetHistory = L.layerGroup();
      lgRef.current.trafficZones = L.layerGroup();
      mapInst.current = map;
      setReady(true);
    };
    if(!document.querySelector('link[href*="leaflet"]')) {
      const l=document.createElement("link"); l.rel="stylesheet"; l.href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"; document.head.appendChild(l);
    }
    if(window.L) { setTimeout(init,50); return; }
    if(!document.querySelector('script[src*="leaflet"]')) {
      const s=document.createElement("script"); s.src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"; s.onload=()=>setTimeout(init,100); document.head.appendChild(s);
    } else { const t=setInterval(()=>{if(window.L){clearInterval(t);init();}},150); }
  },[]);

  // ── Fond de carte selon le thème actif ──
  useEffect(()=>{
    if(!ready||!window.L||!mapInst.current) return;
    const L = window.L;
    if(tileRef.current) mapInst.current.removeLayer(tileRef.current);
    tileRef.current = L.tileLayer(TILES[T.dark?"dark":"light"],{ attribution:ATTR, maxZoom:19 });
    tileRef.current.addTo(mapInst.current);
    tileRef.current.setZIndex(0);
  },[ready, T.dark]);

  // ── Calque trafic temps réel (TomTom) ──
  useEffect(()=>{
    if(!ready||!window.L||!mapInst.current) return;
    const L = window.L;
    if(trafficTileRef.current){ mapInst.current.removeLayer(trafficTileRef.current); trafficTileRef.current=null; }
    if(layers.traffic && trafficAvailable){
      trafficTileRef.current = L.tileLayer(tomtomApi.tileUrlTemplate(), { maxZoom:19, opacity:0.65 });
      trafficTileRef.current.addTo(mapInst.current);
      trafficTileRef.current.setZIndex(2);
    }
  },[ready, layers.traffic, trafficAvailable]);

  // ── Zones surveillées (cercles + labels) ──
  useEffect(()=>{
    if(!ready||!window.L||!lgRef.current.trafficZones) return;
    const L=window.L, lg=lgRef.current.trafficZones;
    lg.clearLayers();
    if(!layers.traffic) return;
    trafficZones.forEach(z=>{
      const circle = L.circle([z.lat,z.lng], { radius:Number(z.rayon_km)*1000, color:"#f59e0b", weight:2, fillColor:"#f59e0b", fillOpacity:0.08 });
      circle.bindTooltip(`🚦 ${z.nom}`, { permanent:true, direction:"center" });
      lg.addLayer(circle);
    });
  },[ready, layers.traffic, trafficZones]);

  useEffect(()=>{
    if(!ready||!mapInst.current||!lgRef.current.trafficZones) return;
    if(layers.traffic) lgRef.current.trafficZones.addTo(mapInst.current);
    else mapInst.current.removeLayer(lgRef.current.trafficZones);
  },[ready, layers.traffic]);

  // ── Marqueurs TECHNICIENS ──
  useEffect(()=>{
    if(!ready||!window.L) return;
    const L=window.L, lg=lgRef.current.tech;
    Object.values(tmRef.current).forEach(m=>m.remove());
    tmRef.current={};
    if(!layers.techs||!lg) return;

    techPos.forEach(tp=>{
      if(!tp.lat||!tp.lng) return;
      const color = getColor(tp.user_id);
      const init  = initials(tp.prenom, tp.nom);
      const isAct = active(tp.updated_at);
      const icon  = mkTechIcon(L, color, init, isAct);
      const age   = ageMin(tp.updated_at);
      const veh   = techVehs[tp.user_id];

      const popupEl = document.createElement("div");
      popupEl.style.cssText = "font-family:system-ui,sans-serif;min-width:240px;max-width:270px;padding:4px 2px";
      popupEl.innerHTML = `
        <div style="display:flex;align-items:center;gap:11px;margin-bottom:11px">
          <div style="width:42px;height:42px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:900;color:#fff;flex-shrink:0">${init}</div>
          <div>
            <div style="font-size:15px;font-weight:800;color:#0f172a">${tp.prenom} ${tp.nom}</div>
            <div style="font-size:11px;color:#64748b">${isAct?"🟢 Actif":"⚪ Inactif"} · ${age!==null?(age<1?"À l'instant":age+" min"):"—"}</div>
          </div>
        </div>
        ${tp.inter_titre
          ? `<div style="padding:9px 11px;background:#f0f9ff;border-radius:9px;margin-bottom:9px;font-size:12px;border:1px solid #bae6fd"><strong>🔧 ${tp.inter_titre}</strong>${tp.client_nom?`<br/>👤 ${tp.client_nom}`:""}${tp.client_adresse?`<br/>📍 ${tp.client_adresse}`:""}</div>`
          : `<div style="padding:9px 11px;background:#f0fdf4;border-radius:9px;margin-bottom:9px;font-size:12px;color:#16a34a;border:1px solid #bbf7d0">✅ Disponible</div>`}
        ${veh ? `<div style="font-size:11px;color:#64748b;margin-bottom:7px">🚐 ${veh.marque} ${veh.modele} · <strong>${veh.immatriculation}</strong></div>` : ""}
        <div id="pop-stock-${tp.user_id}" style="font-size:11px;color:#94a3b8;margin-bottom:10px">⏳ Stock en cours…</div>
        <button data-uid="${tp.user_id}" class="map-center-btn" style="width:100%;padding:8px;background:${color};color:#fff;border:none;border-radius:9px;font-size:12px;font-weight:700;cursor:pointer">📍 Centrer + Itinéraire</button>
      `;
      popupEl.querySelector(".map-center-btn").addEventListener("click", ()=>{
        window._mapFocusTech(tp.user_id);
      });

      const marker = L.marker([tp.lat,tp.lng],{icon,zIndexOffset:1000});
      marker.bindPopup(popupEl,{maxWidth:290});
      marker.on("popupopen",()=>{
        loadStock(tp.user_id).then(()=>{
          const el = document.getElementById(`pop-stock-${tp.user_id}`);
          if(!el) return;
          const s = techStocks[tp.user_id];
          if(s) el.innerHTML = s.length ? `📦 Stock: ${s.length} réf. · ${s.reduce((a,x)=>a+x.quantite,0)} unités` : "📦 Stock vide";
          else el.innerHTML = "📦 Aucun véhicule associé";
        });
      });
      marker.on("click",()=>{ setSelectedTech(tp); loadStock(tp.user_id); });
      lg.addLayer(marker);
      tmRef.current[tp.user_id] = marker;
    });
  },[ready, techPos, layers.techs, getColor, techVehs, loadStock]);

  // ── Marqueurs INTERVENTIONS DU JOUR ──
  useEffect(()=>{
    if(!ready||!window.L) return;
    const L=window.L, lg=lgRef.current.int;
    if(!lg) return;
    lg.clearLayers();
    if(!layers.interventions) return;

    filteredInts.forEach(i=>{
      const lat=i.client_lat||i.lat, lng=i.client_lng||i.lng;
      if(!lat||!lng) return;
      const color   = SC[i.statut]||"#8899bb";
      const urgence = i.priorite==="urgente";
      const done    = ["terminee","annulee"].includes(i.statut);
      const icon    = mkIntIcon(L, color, done?"✅":urgence?"🚨":"🔧");
      const addr    = encodeURIComponent(`${i.client_adresse||""} ${i.client_ville||""}`.trim());
      const dateStr = i.date_planifiee ? new Date(i.date_planifiee).toLocaleDateString("fr-FR",{day:"numeric",month:"short"})+" "+fmtTime(i.date_planifiee) : "—";
      const popup = `<div style="font-family:system-ui,sans-serif;min-width:230px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:9px">
          <div style="width:10px;height:10px;border-radius:50%;background:${color};flex-shrink:0"></div>
          <div style="font-size:14px;font-weight:800;color:#0f172a;flex:1">${i.titre}</div>
        </div>
        <div style="font-size:11px;color:${color};font-weight:700;margin-bottom:7px">${SL[i.statut]||i.statut} · ${i.priorite}</div>
        <div style="font-size:12px;color:#475569;line-height:1.8">
          👤 ${i.client_nom||"—"}<br/>
          📍 ${i.client_adresse||""} ${i.client_ville||""}<br/>
          🔧 ${i.technicien_nom||"<span style=color:#ef4444>Non assigné</span>"}
          <br/>🗓 ${dateStr}
        </div>
        <div style="display:flex;gap:7px;margin-top:11px">
          <a href="https://maps.google.com/?q=${addr}" target="_blank" style="flex:1;padding:7px;background:#4285f4;color:#fff;text-decoration:none;border-radius:8px;font-size:11px;font-weight:700;text-align:center">🚗 Maps</a>
          <a href="https://waze.com/ul?q=${addr}&navigate=yes" target="_blank" style="flex:1;padding:7px;background:#33ccff;color:#0f172a;text-decoration:none;border-radius:8px;font-size:11px;font-weight:700;text-align:center">🔵 Waze</a>
        </div>
      </div>`;
      L.marker([lat,lng],{icon}).bindPopup(popup).addTo(lg);
    });
  },[ready, filteredInts, layers.interventions]);

  // ── Marqueurs SAV ──
  useEffect(()=>{
    if(!ready||!window.L) return;
    const L=window.L, lg=lgRef.current.sav;
    if(!lg) return;
    lg.clearLayers();
    if(!layers.sav) return;

    unassignedSAV.forEach(sav=>{
      if(!sav.lat||!sav.lng) return;
      const color = PC[sav.priorite]||"#f5a623";
      const icon  = mkSAVIcon(L, color);
      const popup = `<div style="font-family:system-ui,sans-serif;min-width:220px">
        <div style="font-size:14px;font-weight:800;color:#0f172a;margin-bottom:7px">🎫 ${sav.objet||sav.titre||"SAV"}</div>
        <div style="display:inline-block;padding:3px 9px;border-radius:20px;background:${color}22;color:${color};font-size:11px;font-weight:700;margin-bottom:8px">⚠ Non assigné · ${sav.priorite}</div>
        <div style="font-size:12px;color:#475569">📍 ${sav.ville||"—"}<br/>${sav.description?sav.description.slice(0,80)+"…":""}</div>
        <button data-savid="${sav.id}" class="map-assign-btn" style="margin-top:11px;width:100%;padding:8px;background:#f59e0b;color:#fff;border:none;border-radius:8px;font-size:12px;font-weight:700;cursor:pointer">⚡ Assigner</button>
      </div>`;
      const m = L.marker([sav.lat,sav.lng],{icon});
      m.bindPopup(popup);
      m.on("popupopen",()=>{
        setTimeout(()=>{
          const btn = document.querySelector(`[data-savid="${sav.id}"]`);
          if(btn) btn.addEventListener("click",()=>{ setAssignTarget(sav); m.closePopup(); });
        },50);
      });
      m.on("click",()=>setAssignTarget(null));
      lg.addLayer(m);
    });
  },[ready, unassignedSAV, layers.sav]);

  // ── Marqueurs VÉHICULES FLOTTE (Océan) — diffing + glisse en douceur, pas de reset brutal ──
  useEffect(()=>{
    if(!ready||!window.L||!lgRef.current.fleet) return;
    if(!layers.fleet||fleetMode!=="live") { lgRef.current.fleet.clearLayers(); fmRef.current={}; return; }
    const L=window.L, lg=lgRef.current.fleet;
    const seenIds = new Set();

    oceanTechs.forEach(tp=>{
      if(tp.latitude==null||tp.longitude==null) return;
      seenIds.add(tp.vehicle_id);
      const color  = FLEET_STATUS_COLOR[tp.status] || FLEET_STATUS_COLOR.disponible;
      const isAct  = isFleetFresh(tp.last_update);
      const existing = fmRef.current[tp.vehicle_id];

      const popupEl = document.createElement("div");
      popupEl.style.cssText = "font-family:system-ui,sans-serif;min-width:230px;padding:4px 2px";
      popupEl.innerHTML = `
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
          <div style="width:38px;height:38px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:900;color:#fff">${fleetInitials(tp.name)}</div>
          <div>
            <div style="font-size:14px;font-weight:800;color:#0f172a">${tp.name}</div>
            <div style="font-size:11px;color:#64748b">${FLEET_STATUS_LABEL[tp.status]||tp.status} · ${fmtFleetAge(tp.last_update)}</div>
          </div>
        </div>
        ${tp.vehicle_plate?`<div style="font-size:11px;color:#64748b;margin-bottom:6px">🚐 <strong>${tp.vehicle_plate}</strong>${tp.speed?` · ${Math.round(tp.speed)} km/h`:""}</div>`:""}
        ${tp.fuel_level!=null?`<div style="font-size:11px;color:${fuelColor(tp.fuel_level)};font-weight:700;margin-bottom:10px">${fmtFuel(tp.fuel_level)}</div>`:""}
        <button data-fid="${tp.vehicle_id}" class="fleet-assign-btn" style="width:100%;padding:8px;background:${color};color:#fff;border:none;border-radius:9px;font-size:12px;font-weight:700;cursor:pointer">📋 Affecter une intervention</button>
      `;
      popupEl.querySelector(".fleet-assign-btn").addEventListener("click", () => { setFleetSelected(tp); setSelectedTech(null); });

      if(existing){
        animateFleetMarkerTo(L, existing, [tp.latitude, tp.longitude]);
        existing.setIcon(mkFleetIcon(L, color, tp.speed||0, isAct, false));
        existing.setPopupContent(popupEl);
        existing.off("click").on("click", () => { setFleetSelected(tp); setSelectedTech(null); });
      } else {
        const marker = L.marker([tp.latitude, tp.longitude], { icon: mkFleetIcon(L, color, tp.speed||0, isAct, true), zIndexOffset:1000 });
        marker.bindPopup(popupEl, { maxWidth:270 });
        marker.on("click", () => { setFleetSelected(tp); setSelectedTech(null); });
        lg.addLayer(marker);
        fmRef.current[tp.vehicle_id] = marker;
      }
    });

    Object.keys(fmRef.current).forEach(id=>{
      if(!seenIds.has(id)){ lg.removeLayer(fmRef.current[id]); delete fmRef.current[id]; }
    });
  },[ready, oceanTechs, layers.fleet, fleetMode]);

  // ── Historique / parcours flotte : polyline + points départ/arrivée/intermédiaires ──
  useEffect(()=>{
    if(!ready||!window.L||!lgRef.current.fleetHistory||!mapInst.current) return;
    const L=window.L, lg=lgRef.current.fleetHistory, map=mapInst.current;
    const show = layers.fleet && fleetMode==="history";
    if(!show){ if(map.hasLayer(lg)) map.removeLayer(lg); lg.clearLayers(); return; }
    if(!map.hasLayer(lg)) map.addLayer(lg);
    lg.clearLayers();
    if(!histPoints.length) return;

    const latlngs = histPoints.map(p => [p.latitude, p.longitude]);
    L.polyline(latlngs, { color:"#3b82f6", weight:4, opacity:.85, lineJoin:"round" }).addTo(lg);

    histPoints.forEach((p, i) => {
      const isStart = i === 0, isEnd = i === histPoints.length - 1;
      const time = new Date(p.recorded_at).toLocaleTimeString("fr-FR", { hour:"2-digit", minute:"2-digit" });
      if (isStart || isEnd) {
        const color = isStart ? "#22c55e" : "#ef4444";
        const marker = L.circleMarker([p.latitude, p.longitude], { radius:9, color:"#fff", weight:2, fillColor:color, fillOpacity:1 }).addTo(lg);
        marker.bindPopup(`<div style="font-family:system-ui,sans-serif;font-size:12px"><strong>${isStart?"🟢 Départ":"🔴 Dernier point"}</strong><br/>${time}</div>`);
      } else {
        L.circleMarker([p.latitude, p.longitude], { radius:4, color:"#fff", weight:1, fillColor:"#3b82f6", fillOpacity:.9 })
          .bindPopup(`<div style="font-family:system-ui,sans-serif;font-size:12px">${time}${p.status?` · ${FLEET_STATUS_LABEL[p.status]||p.status}`:""}</div>`)
          .addTo(lg);
      }
    });

    map.fitBounds(L.latLngBounds(latlngs).pad(0.15), { animate:true });
  },[ready, layers.fleet, fleetMode, histPoints]);

  // ── Secteurs (cercles de proximité autour de chaque tech) ──
  useEffect(()=>{
    if(!ready||!window.L) return;
    const L=window.L, lg=lgRef.current.sector;
    if(!lg) return;
    lg.clearLayers();
    if(!layers.sectors) return;
    techPos.forEach(tp=>{
      if(!tp.lat||!tp.lng) return;
      const color = getColor(tp.user_id);
      L.circle([tp.lat,tp.lng],{ radius:8000, color, fillColor:color, fillOpacity:0.06, weight:1.5, dashArray:"6 4", opacity:0.4 }).addTo(lg);
    });
  },[ready, techPos, layers.sectors, getColor]);

  // ── Route / itinéraire du technicien sélectionné ──
  const drawRoute = useCallback((tp)=>{
    if(!ready||!window.L||!tp) return;
    const L=window.L, lg=lgRef.current.route;
    if(!lg) return;
    lg.clearLayers();
    if(!tp.lat||!tp.lng) return;
    const color = getColor(tp.user_id);
    const myInts = filteredInts
      .filter(i=>String(i.technicien_id)===String(tp.user_id) && !["terminee","annulee"].includes(i.statut))
      .sort((a,b)=>new Date(a.date_planifiee)-new Date(b.date_planifiee));
    if(!myInts.length) return;

    const pts = [[tp.lat,tp.lng]];
    myInts.forEach(i=>{ const lat=i.client_lat||i.lat, lng=i.client_lng||i.lng; if(lat&&lng) pts.push([lat,lng]); });
    if(pts.length<2) return;

    L.polyline(pts,{ color, weight:3, dashArray:"10 7", opacity:.8 }).addTo(lg);
    pts.slice(1).forEach((pt,idx)=>{ L.marker(pt,{icon:mkNumIcon(L,color,idx+1),zIndexOffset:600}).addTo(lg); });
  },[ready, filteredInts, getColor]);

  useEffect(()=>{
    if(!ready) return;
    if(selectedTech) drawRoute(selectedTech);
    else lgRef.current.route?.clearLayers();
  },[selectedTech, drawRoute, ready]);

  // ── Temps de trajet avec trafic temps réel (TomTom) vers l'intervention en cours du technicien sélectionné ──
  useEffect(()=>{
    if(!selectedTech || !trafficAvailable){ setInterventionEta(null); return; }
    const target = filteredInts.find(i=>String(i.technicien_id)===String(selectedTech.user_id)&&i.statut==="en_cours")
      || filteredInts.filter(i=>String(i.technicien_id)===String(selectedTech.user_id)&&i.statut==="planifiee")
           .sort((a,b)=>new Date(a.date_planifiee)-new Date(b.date_planifiee))[0];
    if(!target){ setInterventionEta(null); return; }
    setInterventionEta({ loading:true, error:null, data:null, target });
    tomtomApi.getEtaForIntervention(target.id)
      .then(data=>setInterventionEta({ loading:false, error:null, data, target }))
      .catch(e=>setInterventionEta({ loading:false, error:e.message, data:null, target }));
  },[selectedTech, trafficAvailable, filteredInts]);

  // ── Visibilité des calques ──
  useEffect(()=>{
    if(!ready||!mapInst.current) return;
    const map=mapInst.current;
    Object.entries(lgRef.current).forEach(([key,lg])=>{
      if(!lg) return;
      if(key==="route"||key==="fleetHistory") return; // gérés séparément
      const show = layers[key==="sector"?"sectors":key] ?? true;
      if(show && !map.hasLayer(lg)) map.addLayer(lg);
      if(!show && map.hasLayer(lg)) map.removeLayer(lg);
    });
  },[ready, layers]);

  // ── Callback global popup ──
  useEffect(()=>{
    window._mapFocusTech = uid => {
      const tp = techPos.find(t=>String(t.user_id)===String(uid));
      if(!tp) return;
      setSelectedTech(tp);
      loadStock(uid);
      if(mapInst.current && tp.lat) mapInst.current.flyTo([tp.lat,tp.lng],14,{animate:true,duration:1.2});
    };
    return ()=>{ delete window._mapFocusTech; };
  },[techPos, loadStock]);

  // ── Assignation SAV → technicien ──
  const assignSAV = async (tp, directTicket = null) => {
    const ticket = directTicket || assignTarget;
    if(!ticket || assigning) return;
    setAssigning(true);
    try {
      const res = await fetch(`${BASE}/tickets/${ticket.id}/assigner`, {
        method:"PATCH", headers:hj(),
        body:JSON.stringify({ technicien_id: tp.user_id }),
      }).then(r=>r.json());
      if(res.error) throw new Error(res.error);
      setToast({ ok:true, msg:`✅ "${ticket.objet||ticket.titre}" → ${tp.prenom} ${tp.nom}` });
      setAssignTarget(null);
    } catch(e) {
      setToast({ ok:false, msg:"❌ Erreur : "+e.message });
    }
    setAssigning(false);
    setTimeout(()=>setToast(null), 3500);
  };

  // ── Ma position ──
  const locateMe = () => {
    navigator.geolocation?.getCurrentPosition(p=>{
      const pos=[p.coords.latitude,p.coords.longitude];
      setMyPos(pos);
      mapInst.current?.flyTo(pos,14,{animate:true,duration:1.2});
    });
  };

  // ── Centrer sur un tech ──
  const centerOnTech = (tp) => {
    setSelectedTech(tp);
    setFleetSelected(null);
    loadStock(tp.user_id);
    if(tp.lat&&tp.lng&&mapInst.current) {
      mapInst.current.flyTo([tp.lat,tp.lng],14,{animate:true,duration:1.2});
      tmRef.current[tp.user_id]?.openPopup();
    }
  };

  // ── Fusion techs (positions + liste RH) ──
  const allTechs = (() => {
    const seen = new Set();
    const list = [];
    techPos.forEach(t=>{ seen.add(String(t.user_id)); list.push(t); });
    (techniciens||[]).forEach(t=>{
      if(!seen.has(String(t.id))) list.push({ user_id:t.id, prenom:t.prenom, nom:t.nom });
    });
    return list;
  })();

  const filteredTechs = allTechs.filter(t=>
    !sideSearch || `${t.prenom} ${t.nom}`.toLowerCase().includes(sideSearch.toLowerCase())
  );

  const inAssignMode = !!assignTarget;

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"calc(100vh - 106px)", minHeight:560 }}>

      {/* ── Toast ── */}
      {toast&&(
        <div style={{ position:"fixed", top:20, right:24, zIndex:9999, padding:"12px 20px", borderRadius:13, fontWeight:700, fontSize:13, color:"#fff", boxShadow:"0 8px 28px rgba(0,0,0,.25)", background:toast.ok?"#10b981":"#ef4444", animation:"escalade-in .3s ease" }}>
          {toast.msg}
        </div>
      )}

      {/* ── Bandeau mode assignation ── */}
      {inAssignMode&&(
        <div style={{ background:"linear-gradient(90deg,#f59e0b,#f97316)", padding:"11px 20px", display:"flex", alignItems:"center", gap:14, flexShrink:0, zIndex:10 }}>
          <span style={{ fontSize:18 }}>⚡</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:800, color:"#fff" }}>Mode assignation active</div>
            <div style={{ fontSize:11, color:"#fef3c7" }}>"{assignTarget.objet||assignTarget.titre}" — Cliquez sur un technicien dans la liste pour assigner</div>
          </div>
          <button onClick={()=>setAssignTarget(null)} style={{ background:"rgba(255,255,255,.2)", border:"none", color:"#fff", borderRadius:9, padding:"7px 15px", fontSize:12, fontWeight:700, cursor:"pointer" }}>✕ Annuler</button>
        </div>
      )}

      {/* ── Bandeau mode vie privée flotte ── */}
      {oceanStatus?.privacy_active&&(
        <div style={{ background:"linear-gradient(90deg,#334155,#0f172a)", padding:"11px 20px", display:"flex", alignItems:"center", gap:14, flexShrink:0, zIndex:10 }}>
          <span style={{ fontSize:18 }}>🔒</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:800, color:"#fff" }}>Mode vie privée actif (flotte)</div>
            <div style={{ fontSize:11, color:"#cbd5e1" }}>Synchro GPS en pause de {oceanStatus.privacy_start} à {oceanStatus.privacy_end} — aucune position n'est suivie ni affichée sur cette plage.</div>
          </div>
        </div>
      )}

      {/* ── Body (sidebar + carte) ── */}
      <div style={{ display:"flex", flex:1, overflow:"hidden", borderRadius:16, border:`1px solid ${T.border}`, background:T.card }}>

        {/* ════════════ SIDEBAR ════════════ */}
        <div style={{ width:292, flexShrink:0, borderRight:`1px solid ${T.border}`, display:"flex", flexDirection:"column", overflowY:"auto", background:T.card }}>

          {/* En-tête */}
          <div style={{ padding:"15px 16px 11px", borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontSize:15, fontWeight:800, color:T.text }}>🗺️ Supervision terrain</div>
            <div style={{ fontSize:11, color:T.muted, marginTop:2, textTransform:"capitalize" }}>{today}</div>
          </div>

          {/* Historique — sélecteur de période */}
          <div style={{ padding:"11px 14px", borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontSize:10, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".7px", marginBottom:8 }}>Période affichée</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:5, marginBottom:8 }}>
              {[["today","📅 Aujourd'hui"],["week","📆 7 derniers j."],["month","🗓 30 derniers j."],["all","📚 Tout l'historique"]].map(([p,lbl])=>(
                <button key={p} onClick={()=>setHistPeriod(p)}
                  style={{ padding:"7px 6px", borderRadius:8, border:`1.5px solid ${histPeriod===p?T.accent:T.border}`, background:histPeriod===p?T.accentDim:"transparent", color:histPeriod===p?T.accent:T.muted, fontSize:11, fontWeight:histPeriod===p?700:500, cursor:"pointer", textAlign:"center" }}>
                  {lbl}
                </button>
              ))}
            </div>
            {/* Filtres statut / tech */}
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}
                style={{ flex:1, minWidth:0, background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:"6px 8px", color:T.text, fontSize:11, outline:"none", cursor:"pointer" }}>
                <option value="all">Tous statuts</option>
                <option value="planifiee">📅 Planifié</option>
                <option value="en_cours">🔵 En cours</option>
                <option value="terminee">✅ Terminé</option>
                <option value="annulee">❌ Annulé</option>
              </select>
              <select value={techFilter} onChange={e=>setTechFilter(e.target.value)}
                style={{ flex:1, minWidth:0, background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:"6px 8px", color:T.text, fontSize:11, outline:"none", cursor:"pointer" }}>
                <option value="all">Tous techs</option>
                {allTechs.map(t=>(<option key={t.user_id} value={t.user_id}>{t.prenom} {t.nom}</option>))}
              </select>
            </div>
            {/* Toggle terminées */}
            <div onClick={()=>setShowTerminated(p=>!p)}
              style={{ display:"flex", alignItems:"center", gap:9, marginTop:8, padding:"7px 9px", borderRadius:9, cursor:"pointer", background:showTerminated?`#22d3a020`:"transparent", border:`1px solid ${showTerminated?"#22d3a055":T.border}` }}>
              <div style={{ width:17, height:17, borderRadius:5, border:`2px solid ${showTerminated?"#22d3a0":T.border}`, background:showTerminated?"#22d3a0":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                {showTerminated&&<svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3L3.5 5.5L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/></svg>}
              </div>
              <span style={{ fontSize:12, color:showTerminated?"#22d3a0":T.muted }}>Inclure terminées / annulées</span>
              <span style={{ marginLeft:"auto", fontSize:10, fontWeight:700, color:showTerminated?"#22d3a0":T.muted, background:`${showTerminated?"#22d3a0":T.border}22`, padding:"2px 8px", borderRadius:20 }}>{filteredInts.length}</span>
            </div>
          </div>

          {/* Calques */}
          <div style={{ padding:"11px 14px", borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontSize:10, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".7px", marginBottom:8 }}>Calques actifs</div>
            {[
              ["techs",        "🧑‍🔧", "Techniciens",       techPos.filter(t=>t.lat).length],
              ["interventions","🔧",  "Interventions",     filteredInts.length],
              ["sav",          "🎫",  "SAV non assignés",  unassignedSAV.filter(t=>t.lat&&t.lng).length],
              ["sectors",      "🗂️",  "Périmètres",        techPos.filter(t=>t.lat).length],
              ["fleet",        "🛰️",  "Véhicules flotte",  oceanTechs.filter(t=>t.latitude!=null).length],
              ...(trafficAvailable ? [["traffic", "🚦", "Trafic temps réel", trafficZones.length]] : []),
            ].map(([key,ic,lbl,cnt])=>(
              <div key={key}
                style={{ display:"flex", alignItems:"center", gap:9, padding:"7px 9px", borderRadius:9, marginBottom:3, background:layers[key]?T.accentDim:"transparent", transition:"background .12s" }}>
                <div onClick={()=>setLayers(p=>({...p,[key]:!p[key]}))} style={{ display:"flex", alignItems:"center", gap:9, flex:1, cursor:"pointer", minWidth:0 }}>
                  <div style={{ width:17, height:17, borderRadius:5, border:`2px solid ${layers[key]?T.accent:T.border}`, background:layers[key]?T.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all .15s" }}>
                    {layers[key]&&<svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3L3.5 5.5L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/></svg>}
                  </div>
                  <span style={{ fontSize:14 }}>{ic}</span>
                  <span style={{ fontSize:12, color:layers[key]?T.accent:T.text, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lbl}</span>
                  <span style={{ fontSize:10, background:`${layers[key]?T.accent:T.border}22`, color:layers[key]?T.accent:T.muted, padding:"2px 8px", borderRadius:20, fontWeight:700, flexShrink:0 }}>{cnt}</span>
                </div>
                {key==="fleet"&&isAdmin&&(
                  <button onClick={openFleetConfig} title="Configurer les véhicules affichés"
                    style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:7, color:T.muted, cursor:"pointer", fontSize:12, width:22, height:22, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginLeft:6 }}>
                    ⚙️
                  </button>
                )}
              </div>
            ))}

            {/* Sous-panneau flotte : Direct / Historique */}
            {layers.fleet&&(
              <div style={{ marginTop:8, paddingTop:10, borderTop:`1px solid ${T.border}` }}>
                <div style={{ display:"flex", gap:5, marginBottom:8 }}>
                  {[["live","🔴 Direct"],["history","🕓 Parcours du jour"]].map(([m,lbl])=>(
                    <button key={m} onClick={()=>{ setFleetMode(m); setFleetSelected(null); }}
                      style={{ flex:1, padding:"6px 5px", borderRadius:8, border:`1.5px solid ${fleetMode===m?T.accent:T.border}`, background:fleetMode===m?T.accentDim:"transparent", color:fleetMode===m?T.accent:T.muted, fontSize:10.5, fontWeight:fleetMode===m?700:500, cursor:"pointer" }}>
                      {lbl}
                    </button>
                  ))}
                </div>

                {fleetMode==="live"&&unassignedInts.length>0&&(
                  <div>
                    <div style={{ fontSize:10, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".7px", marginBottom:6 }}>
                      Interventions à affecter · {unassignedInts.length}
                    </div>
                    {unassignedInts.slice(0,4).map(i=>(
                      <div key={i.id} style={{ padding:"6px 9px", borderRadius:8, marginBottom:3, background:T.bg, border:`1px solid ${T.border}`, fontSize:11, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {i.titre}
                      </div>
                    ))}
                    <div style={{ fontSize:10, color:T.muted, fontStyle:"italic", marginTop:4 }}>💡 Cliquer un véhicule sur la carte pour affecter</div>
                  </div>
                )}

                {fleetMode==="history"&&(
                  <div>
                    <select value={histPlate} onChange={e=>{ setHistPlate(e.target.value); setHistPoints([]); setHistLoaded(false); }}
                      style={{ width:"100%", background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:"7px 9px", color:T.text, fontSize:11.5, outline:"none", cursor:"pointer", marginBottom:7 }}>
                      <option value="">— Choisir un véhicule —</option>
                      {histVehicles.map(v => <option key={v.plate} value={v.plate}>{v.name} · {v.plate}</option>)}
                    </select>
                    <input type="date" value={histDate} min={minHistDate} max={todayStr}
                      onChange={e=>{ setHistDate(e.target.value); setHistPoints([]); setHistLoaded(false); }}
                      style={{ width:"100%", background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:"7px 9px", color:T.text, fontSize:11.5, outline:"none", marginBottom:9, boxSizing:"border-box" }}/>

                    {!histPlate&&<div style={{ padding:10, textAlign:"center", fontSize:11, color:T.muted }}>Sélectionnez un véhicule.</div>}
                    {histPlate&&histLoading&&<div style={{ padding:10, textAlign:"center", fontSize:11, color:T.muted }}>Chargement…</div>}
                    {histPlate&&histError&&<div style={{ padding:9, borderRadius:8, background:"#ef444418", border:"1px solid #ef444440", fontSize:11, color:"#ef4444" }}>{histError}</div>}
                    {histPlate&&histLoaded&&!histLoading&&!histError&&histPoints.length===0&&(
                      <div style={{ padding:10, textAlign:"center", fontSize:11, color:T.muted }}>Aucune position ce jour-là.</div>
                    )}
                    {histSummary&&!histLoading&&(
                      <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:10, padding:"9px 11px" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:T.muted, marginBottom:4 }}><span>🟢 Départ</span><strong style={{ color:T.text }}>{new Date(histSummary.start).toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"})}</strong></div>
                        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:T.muted, marginBottom:4 }}><span>🔴 Dernier point</span><strong style={{ color:T.text }}>{new Date(histSummary.end).toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"})}</strong></div>
                        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:T.muted }}><span>📏 Distance</span><strong style={{ color:T.text }}>{histSummary.distanceKm} km</strong></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Recherche techniciens */}
          <div style={{ padding:"10px 13px", borderBottom:`1px solid ${T.border}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, background:T.bg, border:`1px solid ${T.border}`, borderRadius:9, padding:"8px 11px" }}>
              <span style={{ color:T.muted, fontSize:13 }}>🔍</span>
              <input value={sideSearch} onChange={e=>setSideSearch(e.target.value)} placeholder="Rechercher un technicien…"
                style={{ background:"none", border:"none", outline:"none", color:T.text, fontSize:12, flex:1 }}/>
              {sideSearch&&<button onClick={()=>setSideSearch("")} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer" }}>✕</button>}
            </div>
          </div>

          {/* Liste techniciens */}
          <div style={{ flex:1, overflowY:"auto" }}>
            <div style={{ padding:"10px 14px 4px", fontSize:10, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".7px" }}>
              Techniciens · {filteredTechs.length}
            </div>

            {filteredTechs.map(tp=>{
              const color    = getColor(tp.user_id);
              const init     = initials(tp.prenom, tp.nom);
              const isOnline = online(tp.updated_at);
              const isAct    = active(tp.updated_at);
              const veh      = techVehs[tp.user_id];
              const stock    = techStocks[tp.user_id];
              const myTodayInts = todayInts.filter(i=>String(i.technicien_id)===String(tp.user_id));
              const nextInt  = myTodayInts.sort((a,b)=>new Date(a.date_planifiee)-new Date(b.date_planifiee)).find(i=>i.statut==="planifiee");
              const isSel    = selectedTech?.user_id === tp.user_id;

              return (
                <div key={tp.user_id}
                  onDragOver={e=>{ e.preventDefault(); e.currentTarget.style.background=`${color}18`; }}
                  onDragLeave={e=>{ e.currentTarget.style.background=isSel?T.accentDim:inAssignMode?`#f59e0b0d`:"transparent"; }}
                  onDrop={e=>{ e.preventDefault(); e.currentTarget.style.background=isSel?T.accentDim:"transparent"; if(dragSAV){ const sav=dragSAV; setDragSAV(null); assignSAV(tp, sav); } }}
                  onClick={()=>{ if(inAssignMode){ assignSAV(tp); return; } centerOnTech(tp); }}
                  style={{ padding:"12px 13px", cursor:"pointer", borderBottom:`1px solid ${T.border}18`,
                    background:isSel?T.accentDim:inAssignMode?`#f59e0b0d`:"transparent",
                    borderLeft:`3px solid ${isSel?color:"transparent"}`, transition:"all .12s" }}
                  onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.background=T.bg; }}
                  onMouseLeave={e=>{ e.currentTarget.style.background=isSel?T.accentDim:inAssignMode?`#f59e0b0d`:"transparent"; }}>

                  <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                    {/* Avatar */}
                    <div style={{ position:"relative", flexShrink:0 }}>
                      <div style={{ width:38, height:38, borderRadius:"50%", background:color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:900, color:"#fff", border:`2px solid ${isSel?"#fff":T.card}` }}>{init}</div>
                      <div style={{ position:"absolute", bottom:0, right:0, width:10, height:10, borderRadius:"50%", background:isAct?"#22d3a0":isOnline?"#f59e0b":"#94a3b8", border:`2px solid ${T.card}` }}/>
                    </div>

                    {/* Infos */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{tp.prenom} {tp.nom}</div>
                      <div style={{ fontSize:11, color:T.muted, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {tp.inter_titre ? `🔧 ${tp.inter_titre}` : isOnline ? "✅ Disponible" : "⚪ Hors ligne"}
                      </div>
                      {nextInt&&(
                        <div style={{ fontSize:10, color:T.accent, marginTop:2 }}>
                          ⏭ {fmtTime(nextInt.date_planifiee)} · {nextInt.client_ville||nextInt.client_nom||nextInt.titre}
                        </div>
                      )}
                      {veh&&(
                        <div style={{ fontSize:10, color:T.muted, marginTop:1 }}>
                          🚐 {veh.marque} {veh.modele} · {veh.immatriculation}
                          {stock&&` · 📦 ${stock.length} réf.`}
                        </div>
                      )}
                      {myTodayInts.length>0&&(
                        <div style={{ fontSize:10, color:T.muted, marginTop:2 }}>
                          {myTodayInts.filter(i=>i.statut==="terminee").length}/{myTodayInts.length} faites aujourd'hui
                        </div>
                      )}
                    </div>

                    {/* Centrer */}
                    {tp.lat&&(
                      <button onClick={e=>{ e.stopPropagation(); centerOnTech(tp); }}
                        title="Centrer sur la carte"
                        style={{ padding:"5px 8px", borderRadius:7, background:T.accentDim, border:`1px solid ${T.border}`, color:T.accent, fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                        📍
                      </button>
                    )}
                  </div>

                  {inAssignMode&&(
                    <div style={{ marginTop:7, padding:"5px 10px", background:"#f59e0b18", border:"1px solid #f59e0b35", borderRadius:8, fontSize:11, color:"#d97706", fontWeight:600, textAlign:"center" }}>
                      👆 Cliquer pour assigner {assigning?"…":""}
                    </div>
                  )}
                </div>
              );
            })}

            {/* ── SAV non assignés (drag source) ── */}
            {unassignedSAV.length>0&&(
              <div style={{ borderTop:`1px solid ${T.border}`, marginTop:4 }}>
                <div style={{ padding:"10px 14px 4px", fontSize:10, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".7px" }}>
                  SAV à assigner · {unassignedSAV.length}
                </div>
                {unassignedSAV.map(sav=>{
                  const color = PC[sav.priorite]||"#f5a623";
                  const isTarg = assignTarget?.id===sav.id;
                  return (
                    <div key={sav.id}
                      draggable={true}
                      onDragStart={()=>{ setDragSAV(sav); setAssignTarget(sav); }}
                      onDragEnd  ={()=>setDragSAV(null)}
                      onClick    ={()=>{ setAssignTarget(isTarg?null:sav); if(!isTarg&&sav.lat&&sav.lng) mapInst.current?.flyTo([sav.lat,sav.lng],14,{animate:true}); }}
                      style={{ padding:"10px 13px", cursor:"grab", borderBottom:`1px solid ${T.border}14`,
                        background:isTarg?`${color}18`:"transparent",
                        borderLeft:`3px solid ${isTarg?color:"transparent"}`, transition:"all .12s" }}
                      onMouseEnter={e=>{ e.currentTarget.style.background=T.bg; }}
                      onMouseLeave={e=>{ e.currentTarget.style.background=isTarg?`${color}18`:"transparent"; }}>
                      <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                        <div style={{ width:8, height:8, borderRadius:"50%", background:color, flexShrink:0 }}/>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:12, fontWeight:600, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                            {sav.objet||sav.titre||"SAV"}
                          </div>
                          <div style={{ fontSize:10, color:T.muted }}>📍 {sav.ville||"—"} · {sav.priorite}</div>
                        </div>
                        <span style={{ fontSize:14, color:T.muted, cursor:"grab" }}>⠿</span>
                      </div>
                    </div>
                  );
                })}
                <div style={{ padding:"8px 14px 10px", fontSize:10, color:T.muted, fontStyle:"italic" }}>
                  💡 Glisser un ticket sur un technicien pour assigner
                </div>
              </div>
            )}

            {/* ── Résumé stats ── */}
            <div style={{ padding:"14px 13px 18px" }}>
              <div style={{ fontSize:10, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".7px", marginBottom:8 }}>Résumé du jour</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:7 }}>
                {[
                  ["🔧","Interv.",       todayInts.length,                                              T.accent],
                  ["✅","Terminées",     todayInts.filter(i=>i.statut==="terminee").length,             "#22d3a0"],
                  ["🎫","SAV ouverts",   unassignedSAV.length,                                          "#f59e0b"],
                  ["🟢","Techs actifs",  techPos.filter(t=>active(t.updated_at)).length,               "#a855f7"],
                ].map(([ic,lbl,val,c])=>(
                  <div key={lbl} style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:10, padding:"10px 11px", textAlign:"center" }}>
                    <div style={{ fontSize:18 }}>{ic}</div>
                    <div style={{ fontSize:20, fontWeight:900, color:c, lineHeight:1.1 }}>{val}</div>
                    <div style={{ fontSize:10, color:T.muted, marginTop:2 }}>{lbl}</div>
                  </div>
                ))}
              </div>
              <button onClick={locateMe} style={{ width:"100%", marginTop:9, padding:"9px", borderRadius:9, background:myPos?`#a855f720`:T.bg, border:`1px solid ${myPos?"#a855f7":T.border}`, color:myPos?"#a855f7":T.muted, fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
                <span style={{ fontSize:15 }}>📡</span> {myPos?"Localisé ✅":"Me localiser"}
              </button>
            </div>
          </div>
        </div>

        {/* ════════════ CARTE ════════════ */}
        <div style={{ flex:1, position:"relative", overflow:"hidden" }}>
          {!ready&&(
            <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, background:T.bg, zIndex:2 }}>
              <div style={{ fontSize:44 }}>🗺️</div>
              <div style={{ fontSize:14, color:T.muted }}>Chargement de la carte…</div>
              <div style={{ fontSize:11, color:T.muted, opacity:.6 }}>CartoDB · Leaflet 1.9</div>
            </div>
          )}

          <div ref={mapRef} style={{ height:"100%", width:"100%" }}/>

          {/* ── Légende flottante ── */}
          {ready&&(
            <div style={{ position:"absolute", bottom:20, right:20, zIndex:800, background:`${T.card}ee`, border:`1px solid ${T.border}`, borderRadius:12, padding:"11px 14px", backdropFilter:"blur(8px)", boxShadow:"0 4px 16px rgba(0,0,0,.15)" }}>
              <div style={{ fontSize:10, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".7px", marginBottom:8 }}>Légende</div>
              {[
                ["🔧", "#5b6ef8",  "En cours"],
                ["📅", "#f5a623",  "Planifié"],
                ["✅", "#22d3a0",  "Terminé"],
                ["🎫", "#f59e0b",  "SAV · urgente"],
                ["🎫", "#5b6ef8",  "SAV · normale"],
                ["🧑‍🔧","#22d3a0",  "Tech actif (< 5 min)"],
                ["🧑‍🔧","#f59e0b",  "Tech online"],
                ...(layers.fleet ? Object.entries(FLEET_STATUS_LABEL).map(([k,lbl])=>["🚐", FLEET_STATUS_COLOR[k], lbl]) : []),
              ].map(([ic,c,lbl])=>(
                <div key={lbl} style={{ display:"flex", alignItems:"center", gap:7, marginBottom:4 }}>
                  <div style={{ width:9, height:9, borderRadius:"50%", background:c, flexShrink:0 }}/>
                  <span style={{ fontSize:11, color:T.muted }}>{lbl}</span>
                </div>
              ))}
            </div>
          )}

          {/* ── Panel itinéraire du tech sélectionné ── */}
          {selectedTech&&(
            <div style={{ position:"absolute", bottom:20, left:20, zIndex:800, background:`${T.card}f0`, border:`1.5px solid ${getColor(selectedTech.user_id)}44`, borderRadius:14, padding:"13px 15px", backdropFilter:"blur(10px)", boxShadow:"0 8px 28px rgba(0,0,0,.2)", maxWidth:340, minWidth:260 }}>
              <div style={{ display:"flex", alignItems:"center", gap:11, marginBottom:11 }}>
                <div style={{ width:36, height:36, borderRadius:"50%", background:getColor(selectedTech.user_id), display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:900, color:"#fff", flexShrink:0 }}>
                  {initials(selectedTech.prenom,selectedTech.nom)}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:800, color:T.text }}>{selectedTech.prenom} {selectedTech.nom}</div>
                  <div style={{ fontSize:11, color:T.muted }}>
                    {filteredInts.filter(i=>String(i.technicien_id)===String(selectedTech.user_id)).length} intervention(s) · période sélectionnée
                    {techStocks[selectedTech.user_id]&&` · 📦 ${techStocks[selectedTech.user_id].length} réf.`}
                  </div>
                </div>
                <button onClick={()=>{setSelectedTech(null);lgRef.current.route?.clearLayers();}}
                  style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:18, flexShrink:0 }}>✕</button>
              </div>

              {/* Étapes de la tournée */}
              <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:3 }}>
                {filteredInts
                  .filter(i=>String(i.technicien_id)===String(selectedTech.user_id))
                  .sort((a,b)=>new Date(a.date_planifiee)-new Date(b.date_planifiee))
                  .map((i,idx)=>(
                    <div key={i.id} style={{ flexShrink:0, padding:"7px 11px", background:T.bg, border:`1px solid ${SC[i.statut]||T.border}55`, borderRadius:10, minWidth:110, cursor:"pointer" }}
                      onClick={()=>{ const lat=i.client_lat||i.lat, lng=i.client_lng||i.lng; if(lat&&lng) mapInst.current?.flyTo([lat,lng],15,{animate:true}); }}>
                      <div style={{ fontSize:10, fontWeight:700, color:SC[i.statut]||T.muted }}>#{idx+1} {SL[i.statut]||i.statut}</div>
                      <div style={{ fontSize:11, color:T.text, fontWeight:600, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{i.client_ville||i.client_nom||i.titre}</div>
                      {i.date_planifiee&&<div style={{ fontSize:10, color:T.muted }}>🗓 {new Date(i.date_planifiee).toLocaleDateString("fr-FR",{day:"numeric",month:"short"})} {fmtTime(i.date_planifiee)}</div>}
                    </div>
                  ))}
                {filteredInts.filter(i=>String(i.technicien_id)===String(selectedTech.user_id)).length===0&&(
                  <div style={{ fontSize:12, color:T.muted, fontStyle:"italic" }}>Aucune intervention pour cette période</div>
                )}
              </div>

              {interventionEta&&(
                <div style={{ marginTop:11, padding:"9px 12px", background:T.bg, border:`1px solid ${T.border}`, borderRadius:10 }}>
                  {interventionEta.loading ? (
                    <div style={{ fontSize:11.5, color:T.muted }}>🚦 Calcul du temps de trajet…</div>
                  ) : interventionEta.error ? (
                    <div style={{ fontSize:11.5, color:T.muted }}>🚦 {interventionEta.error}</div>
                  ) : (
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:16 }}>🚦</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:800, color:T.text }}>
                          {Math.round(interventionEta.data.travelTimeSec/60)} min vers {interventionEta.target.client_ville||interventionEta.target.client_nom||"l'intervention"}
                        </div>
                        <div style={{ fontSize:10.5, color:T.muted }}>
                          {interventionEta.data.distanceKm} km
                          {interventionEta.data.trafficDelaySec>60?` · +${Math.round(interventionEta.data.trafficDelaySec/60)} min de trafic`:" · trafic fluide"}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Panel véhicule flotte sélectionné : affectation d'une intervention ── */}
          {fleetSelected&&(
            <div style={{ position:"absolute", bottom:20, left:20, zIndex:800, background:`${T.card}f0`, border:`1.5px solid ${(FLEET_STATUS_COLOR[fleetSelected.status]||FLEET_STATUS_COLOR.disponible)}44`, borderRadius:14, padding:"13px 15px", backdropFilter:"blur(10px)", boxShadow:"0 8px 28px rgba(0,0,0,.2)", maxWidth:320, minWidth:270 }}>
              <div style={{ display:"flex", alignItems:"center", gap:11, marginBottom:12 }}>
                <div style={{ width:38, height:38, borderRadius:"50%", background:FLEET_STATUS_COLOR[fleetSelected.status]||FLEET_STATUS_COLOR.disponible, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:900, color:"#fff", flexShrink:0 }}>
                  {fleetInitials(fleetSelected.name)}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:800, color:T.text }}>{fleetSelected.name}</div>
                  <div style={{ fontSize:11, color:T.muted }}>
                    {FLEET_STATUS_LABEL[fleetSelected.status]} · {fmtFleetAge(fleetSelected.last_update)}
                    {fleetSelected.vehicle_plate?` · ${fleetSelected.vehicle_plate}`:""}
                  </div>
                  {fleetSelected.fuel_level!=null && (
                    <div style={{ fontSize:11, color:fuelColor(fleetSelected.fuel_level), fontWeight:700, marginTop:2 }}>{fmtFuel(fleetSelected.fuel_level)}</div>
                  )}
                </div>
                <button onClick={()=>setFleetSelected(null)} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:18, flexShrink:0 }}>✕</button>
              </div>

              <div style={{ fontSize:10, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".7px", marginBottom:7 }}>Affecter à une intervention</div>
              {!fleetSelected.technician_id ? (
                <div style={{ fontSize:11.5, color:"#f59e0b", background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.3)", borderRadius:9, padding:"9px 11px", lineHeight:1.5 }}>
                  ⚠️ Véhicule non rattaché à un technicien du CRM. Vérifiez la correspondance de plaque dans Parc Véhicules avant de pouvoir affecter une intervention.
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:5, maxHeight:160, overflowY:"auto" }}>
                  {unassignedInts.length===0 && <div style={{ fontSize:11.5, color:T.muted, fontStyle:"italic" }}>Aucune intervention non affectée</div>}
                  {unassignedInts.map(i=>(
                    <button key={i.id} disabled={assigning} onClick={()=>assignInterventionToVehicle(fleetSelected, i)}
                      style={{ textAlign:"left", padding:"8px 10px", borderRadius:9, background:T.bg, border:`1px solid ${T.border}`, color:T.text, fontSize:11.5, cursor:"pointer" }}>
                      <div style={{ fontWeight:700 }}>{i.titre}</div>
                      <div style={{ fontSize:10, color:T.muted }}>📍 {i.client_ville||i.client_nom||"—"}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Modale config véhicules flotte (admin) ── */}
      {showFleetConfig && (
        <div onClick={()=>setShowFleetConfig(false)}
          style={{ position:"fixed", inset:0, background:"rgba(8,12,30,0.4)", backdropFilter:"blur(4px)", zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:20, width:420, maxHeight:"80vh", display:"flex", flexDirection:"column", padding:0, overflow:"hidden" }}>
            <div style={{ padding:"18px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:T.text }}>⚙️ Véhicules affichés</div>
                <div style={{ fontSize:11.5, color:T.muted, marginTop:2 }}>Décocher un véhicule le masque de la carte pour tout le monde.</div>
              </div>
              <button onClick={()=>setShowFleetConfig(false)} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:18 }}>✕</button>
            </div>
            <div style={{ overflowY:"auto", padding:"8px 10px" }}>
              {fleetConfigLoading && <div style={{ padding:20, textAlign:"center", fontSize:12.5, color:T.muted }}>Chargement…</div>}
              {!fleetConfigLoading && fleetConfigTechs.length===0 && (
                <div style={{ padding:20, textAlign:"center", fontSize:12.5, color:T.muted }}>Aucun véhicule synchronisé pour le moment.</div>
              )}
              {!fleetConfigLoading && fleetConfigTechs
                .slice()
                .sort((a,b)=>a.name.localeCompare(b.name))
                .map(tp => (
                <label key={tp.vehicle_id}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 10px", borderRadius:10, cursor: tp.vehicle_plate?"pointer":"not-allowed", opacity: tp.vehicle_plate?1:0.5 }}
                  onMouseEnter={e=>e.currentTarget.style.background=T.bg} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <input type="checkbox" checked={!tp.hidden} disabled={!tp.vehicle_plate || togglingPlate===tp.vehicle_plate}
                    onChange={e=>toggleFleetVehicleHidden(tp.vehicle_plate, !e.target.checked)} />
                  <div style={{ width:28, height:28, borderRadius:"50%", background:FLEET_STATUS_COLOR[tp.status]||FLEET_STATUS_COLOR.disponible, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:900, color:"#fff", flexShrink:0 }}>
                    {fleetInitials(tp.name)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12.5, fontWeight:700, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{tp.name}</div>
                    <div style={{ fontSize:10.5, color:T.muted }}>{tp.vehicle_plate || "sans plaque — non masquable"}</div>
                  </div>
                  {tp.hidden && <span style={{ fontSize:10, fontWeight:700, color:T.muted, background:T.bg, border:`1px solid ${T.border}`, borderRadius:6, padding:"2px 7px" }}>Masqué</span>}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
